import type { ComponentPropsWithRef, ReactNode } from "react";
import { forwardRef } from "react";
import { Column, Row, Section } from "@react-email/components";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { useTopBlockStyles } from "../../../Design/useTopBlockStyles";
import { defaultStyles } from "./defaultStyles";

export interface HeroBlock1Props extends ComponentPropsWithRef<"table"> {
  headerContent?: ReactNode;
  imageContent?: ReactNode;
  primaryButton?: ReactNode;
  secondaryButton?: ReactNode;
  styles?: Record<string, StyleValue>;
}

export const HeroBlock1 = forwardRef<HTMLTableElement, HeroBlock1Props>(
  (
    {
      headerContent,
      primaryButton,
      secondaryButton,
      styles = {},
      imageContent,
      ...rest
    },
    ref,
  ) => {
    const { backgroundStyles, paddingStyles, marginStyles } = useTopBlockStyles(
      {
        styles,
        defaultStyles,
        ignoreDefaults: false,
      },
    );

    const defaultSectionProps = {
      align: "center",
      width: "auto",
    };

    const primarySectionProps = {
      align: "right",
      width: "auto",
    };

    const secondarySectionProps = {
      align: "left",
      width: "auto",
    };

    return (
      <Section
        ref={ref}
        style={{
          ...backgroundStyles,
          ...paddingStyles,
          ...marginStyles,
          textAlign: "center",
        }}
        {...rest}
      >
        <Row>
          <Column style={{ paddingBottom: "24px" }}>{headerContent}</Column>
        </Row>

        <Row>
          <Column>
            <Section {...defaultSectionProps}>
              <Row>
                <Column align="center">
                  <Section
                    style={{ display: "inline-block" }}
                    {...primarySectionProps}
                  >
                    <Row>
                      <Column style={{ paddingRight: "16px" }}>
                        {primaryButton}
                      </Column>
                    </Row>
                  </Section>
                </Column>
                <Column align="center">
                  <Section
                    style={{ display: "inline-block" }}
                    {...secondarySectionProps}
                  >
                    <Row>
                      <Column>{secondaryButton}</Column>
                    </Row>
                  </Section>
                </Column>
              </Row>
            </Section>
          </Column>
        </Row>

        <Row>
          <Column style={{ paddingTop: "48px" }}>{imageContent}</Column>
        </Row>
      </Section>
    );
  },
);

HeroBlock1.displayName = "HeroBlock1";
